import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

// Enhanced kitchen monitoring interfaces
export interface KitchenOrder {
  id: string;
  orderNumber: string;
  tableNumber: number;
  items: {
    name: string;
    quantity: number;
    station: 'grill' | 'salad' | 'pizza' | 'dessert' | 'drinks';
    prepTime: number;
    startedAt?: Date;
    completedAt?: Date;
    assignedChef?: string;
    complexity: 'simple' | 'medium' | 'complex';
  }[];
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'preparing' | 'ready' | 'served';
  orderTime: Date;
  estimatedCompletion: Date;
  actualCompletion?: Date;
  customerWaitTime: number;
  isDelayed: boolean;
  delayReason?: string;
  customerId: string;
  serverId: string;
  totalValue: number;
}

export interface ChefPerformance {
  id: string;
  name: string;
  station: string;
  currentOrders: number;
  completedToday: number;
  averageTime: number;
  efficiency: number;
  status: 'active' | 'break' | 'offline';
  lastActive: Date;
  shiftStart: Date;
  shiftEnd: Date;
  todayStats: {
    hoursWorked: number;
    ordersCompleted: number;
    averageOrderTime: number;
    errorCount: number;
    customerRating: number;
    rushHourPerformance: number;
    complexOrdersHandled: number;
  };
  weeklyTrends: {
    efficiency: number[];
    orderVolume: number[];
    errorRate: number[];
  };
}

export interface KitchenStation {
  id: string;
  name: string;
  type: 'grill' | 'salad' | 'pizza' | 'dessert' | 'drinks' | 'prep';
  currentLoad: number;
  maxCapacity: number;
  temperature?: number;
  optimalTemp?: number;
  status: 'operational' | 'warning' | 'error' | 'maintenance';
  assignedChefs: string[];
  queueLength: number;
  avgProcessingTime: number;
  lastMaintenance: Date;
  nextMaintenance: Date;
  equipmentHealth: number; // 0-100%
  utilizationRate: number; // 0-100%
  energyEfficiency: number; // 0-100%
}

export interface BottleneckAlert {
  id: string;
  type: 'station_overload' | 'chef_overwhelmed' | 'equipment_failure' | 'delay_cascade' | 'ingredient_shortage' | 'quality_issue';
  severity: 'low' | 'medium' | 'high' | 'critical';
  station?: string;
  chef?: string;
  equipment?: string;
  ingredient?: string;
  description: string;
  impact: string;
  recommendation: string;
  estimatedResolutionTime: number; // minutes
  timestamp: Date;
  resolved: boolean;
  resolvedAt?: Date;
  resolvedBy?: string;
  autoResolvable: boolean;
}

export interface KitchenMetrics {
  timestamp: Date;
  ordersPerHour: number;
  averageOrderTime: number;
  customerSatisfactionScore: number;
  kitchenEfficiency: number;
  wastePercentage: number;
  energyConsumption: number;
  peakHours: { hour: number; orderCount: number }[];
  rushPeriods: { start: Date; end: Date; intensity: number }[];
  qualityScore: number;
  inventoryTurnover: number;
}

export interface PerformanceTrend {
  date: Date;
  metric: string;
  value: number;
  target: number;
  variance: number;
  trend: 'improving' | 'stable' | 'declining';
}

export interface PredictiveAlert {
  id: string;
  type: 'volume_spike' | 'staff_shortage' | 'equipment_failure' | 'supply_shortage';
  probability: number; // 0-1
  expectedTime: Date;
  description: string;
  impact: string;
  preventiveActions: string[];
  confidence: number; // 0-1
}

// Kitchen Analytics Store
interface KitchenAnalyticsState {
  // Real-time data
  activeOrders: KitchenOrder[];
  chefPerformance: ChefPerformance[];
  kitchenStations: KitchenStation[];
  currentMetrics: KitchenMetrics | null;
  
  // Alerts and monitoring
  bottleneckAlerts: BottleneckAlert[];
  predictiveAlerts: PredictiveAlert[];
  
  // Historical data
  performanceTrends: PerformanceTrend[];
  historicalMetrics: KitchenMetrics[];
  
  // Configuration
  refreshInterval: number;
  isLiveMode: boolean;
  soundEnabled: boolean;
  alertThresholds: {
    maxWaitTime: number;
    minEfficiency: number;
    maxStationLoad: number;
    criticalDelayTime: number;
  };
  
  // UI State
  selectedTimeRange: 'hour' | 'day' | 'week' | 'month';
  selectedStation: string | null;
  selectedChef: string | null;
  isLoading: boolean;
  error: string | null;
}

interface KitchenAnalyticsActions {
  // Real-time updates
  updateActiveOrders: (orders: KitchenOrder[]) => void;
  updateChefPerformance: (performance: ChefPerformance[]) => void;
  updateKitchenStations: (stations: KitchenStation[]) => void;
  updateCurrentMetrics: (metrics: KitchenMetrics) => void;
  
  // Order management
  addOrder: (order: KitchenOrder) => void;
  updateOrderStatus: (orderId: string, status: KitchenOrder['status'], completedAt?: Date) => void;
  markOrderDelayed: (orderId: string, reason: string) => void;
  
  // Alert management
  addBottleneckAlert: (alert: Omit<BottleneckAlert, 'id' | 'timestamp'>) => void;
  resolveBottleneckAlert: (alertId: string, resolvedBy: string) => void;
  addPredictiveAlert: (alert: Omit<PredictiveAlert, 'id'>) => void;
  
  // Analytics
  getKitchenEfficiencyTrend: (timeRange: 'day' | 'week' | 'month') => number[];
  getStationUtilization: () => { station: string; utilization: number }[];
  getChefPerformanceComparison: () => { chef: string; efficiency: number; orders: number }[];
  getPredictedRushHours: () => { hour: number; predictedOrders: number }[];
  
  // Bottleneck analysis
  analyzeBottlenecks: () => {
    currentBottlenecks: BottleneckAlert[];
    riskFactors: { factor: string; risk: number }[];
    recommendations: string[];
  };
  
  // Performance optimization
  getOptimizationSuggestions: () => {
    staffing: string[];
    equipment: string[];
    process: string[];
    inventory: string[];
  };
  
  // Configuration
  setRefreshInterval: (interval: number) => void;
  toggleLiveMode: () => void;
  toggleSound: () => void;
  updateAlertThresholds: (thresholds: Partial<KitchenAnalyticsState['alertThresholds']>) => void;
  
  // UI actions
  setSelectedTimeRange: (range: 'hour' | 'day' | 'week' | 'month') => void;
  setSelectedStation: (stationId: string | null) => void;
  setSelectedChef: (chefId: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

type KitchenAnalyticsStore = KitchenAnalyticsState & KitchenAnalyticsActions;

// Mock data generators
// Create the kitchen analytics store
export const useKitchenAnalyticsStore = create<KitchenAnalyticsStore>()(
  persist(
    immer((set, get) => ({
      // Initial state
      activeOrders: [], // Don't auto-initialize with mock data
      chefPerformance: [], // Don't auto-initialize with mock data
      kitchenStations: [], // Don't auto-initialize with mock data
      currentMetrics: null,
      bottleneckAlerts: [],
      predictiveAlerts: [],
      performanceTrends: [],
      historicalMetrics: [],
      refreshInterval: 5,
      isLiveMode: true,
      soundEnabled: true,
      alertThresholds: {
        maxWaitTime: 20,
        minEfficiency: 75,
        maxStationLoad: 90,
        criticalDelayTime: 15
      },
      selectedTimeRange: 'day',
      selectedStation: null,
      selectedChef: null,
      isLoading: false,
      error: null,

      // Actions
      updateActiveOrders: (orders: KitchenOrder[]) => {
        set(draft => {
          draft.activeOrders = orders;
        });
      },

      updateChefPerformance: (performance: ChefPerformance[]) => {
        set(draft => {
          draft.chefPerformance = performance;
        });
      },

      updateKitchenStations: (stations: KitchenStation[]) => {
        set(draft => {
          draft.kitchenStations = stations;
        });
      },

      updateCurrentMetrics: (metrics: KitchenMetrics) => {
        set(draft => {
          draft.currentMetrics = metrics;
          draft.historicalMetrics.push(metrics);
          
          // Keep only last 1000 metrics for performance
          if (draft.historicalMetrics.length > 1000) {
            draft.historicalMetrics = draft.historicalMetrics.slice(-1000);
          }
        });
      },

      addOrder: (order: KitchenOrder) => {
        set(draft => {
          draft.activeOrders.push(order);
        });
      },

      updateOrderStatus: (orderId: string, status: KitchenOrder['status'], completedAt?: Date) => {
        set(draft => {
          const order = draft.activeOrders.find(o => o.id === orderId);
          if (order) {
            order.status = status;
            if (completedAt) {
              order.actualCompletion = completedAt;
            }
          }
        });
      },

      markOrderDelayed: (orderId: string, reason: string) => {
        set(draft => {
          const order = draft.activeOrders.find(o => o.id === orderId);
          if (order) {
            order.isDelayed = true;
            order.delayReason = reason;
          }
        });
      },

      addBottleneckAlert: (alert: Omit<BottleneckAlert, 'id' | 'timestamp'>) => {
        set(draft => {
          const newAlert: BottleneckAlert = {
            ...alert,
            id: `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date(),
          };
          draft.bottleneckAlerts.unshift(newAlert);
        });
      },

      resolveBottleneckAlert: (alertId: string, resolvedBy: string) => {
        set(draft => {
          const alert = draft.bottleneckAlerts.find(a => a.id === alertId);
          if (alert) {
            alert.resolved = true;
            alert.resolvedAt = new Date();
            alert.resolvedBy = resolvedBy;
          }
        });
      },

      addPredictiveAlert: (alert: Omit<PredictiveAlert, 'id'>) => {
        set(draft => {
          const newAlert: PredictiveAlert = {
            ...alert,
            id: `pred-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          };
          draft.predictiveAlerts.push(newAlert);
        });
      },

      getKitchenEfficiencyTrend: (timeRange: 'day' | 'week' | 'month') => {
        const { historicalMetrics } = get();
        const now = new Date();
        let startTime: Date;
        
        switch (timeRange) {
          case 'day':
            startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            break;
          case 'week':
            startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case 'month':
            startTime = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
        }
        
        return historicalMetrics
          .filter(m => m.timestamp >= startTime)
          .map(m => m.kitchenEfficiency);
      },

      getStationUtilization: () => {
        const { kitchenStations } = get();
        return kitchenStations.map(station => ({
          station: station.name,
          utilization: station.utilizationRate
        }));
      },

      getChefPerformanceComparison: () => {
        const { chefPerformance } = get();
        return chefPerformance.map(chef => ({
          chef: chef.name,
          efficiency: chef.efficiency,
          orders: chef.completedToday
        }));
      },

      getPredictedRushHours: () => {
        // Mock predictive data - in real app would use ML algorithms
        return [
          { hour: 12, predictedOrders: 25 },
          { hour: 13, predictedOrders: 28 },
          { hour: 18, predictedOrders: 22 },
          { hour: 19, predictedOrders: 30 },
          { hour: 20, predictedOrders: 18 }
        ];
      },

      analyzeBottlenecks: () => {
        const { bottleneckAlerts, kitchenStations, chefPerformance } = get();
        
        const currentBottlenecks = bottleneckAlerts.filter(alert => !alert.resolved);
        
        const riskFactors = [
          { factor: 'Station Overload', risk: kitchenStations.filter(s => s.utilizationRate > 85).length / kitchenStations.length },
          { factor: 'Chef Stress', risk: chefPerformance.filter(c => c.currentOrders > 3).length / chefPerformance.length },
          { factor: 'Equipment Health', risk: 1 - (kitchenStations.reduce((acc, s) => acc + s.equipmentHealth, 0) / kitchenStations.length / 100) }
        ];
        
        const recommendations = [
          'Consider redistributing orders from overloaded stations',
          'Monitor chef performance during peak hours',
          'Schedule preventive maintenance for aging equipment'
        ];
        
        return { currentBottlenecks, riskFactors, recommendations };
      },

      getOptimizationSuggestions: () => {
        const { kitchenStations, chefPerformance } = get();
        
        return {
          staffing: [
            'Add prep chef during peak hours (12-2 PM)',
            'Cross-train staff for station flexibility'
          ],
          equipment: [
            'Upgrade grill station capacity',
            'Install temperature monitoring sensors'
          ],
          process: [
            'Implement priority queuing system',
            'Optimize ingredient prep schedules'
          ],
          inventory: [
            'Monitor high-turnover ingredients more closely',
            'Implement just-in-time ordering'
          ]
        };
      },

      // Configuration actions
      setRefreshInterval: (interval: number) => {
        set(draft => {
          draft.refreshInterval = interval;
        });
      },

      toggleLiveMode: () => {
        set(draft => {
          draft.isLiveMode = !draft.isLiveMode;
        });
      },

      toggleSound: () => {
        set(draft => {
          draft.soundEnabled = !draft.soundEnabled;
        });
      },

      updateAlertThresholds: (thresholds) => {
        set(draft => {
          Object.assign(draft.alertThresholds, thresholds);
        });
      },

      // UI actions
      setSelectedTimeRange: (range) => {
        set(draft => {
          draft.selectedTimeRange = range;
        });
      },

      setSelectedStation: (stationId) => {
        set(draft => {
          draft.selectedStation = stationId;
        });
      },

      setSelectedChef: (chefId) => {
        set(draft => {
          draft.selectedChef = chefId;
        });
      },

      setLoading: (loading) => {
        set(draft => {
          draft.isLoading = loading;
        });
      },

      setError: (error) => {
        set(draft => {
          draft.error = error;
        });
      }
    })),
    {
      name: 'nova-kitchen-analytics',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        alertThresholds: state.alertThresholds,
        refreshInterval: state.refreshInterval,
        soundEnabled: state.soundEnabled,
        selectedTimeRange: state.selectedTimeRange,
      }),
    }
  )
);